import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { maximumMediaWidth, maximumWidth, } from '@polestar/component-warehouse-react/tokens/breakpoints/Breakpoints';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import useStaticViewportHeight from '@polestar/component-warehouse-react/hooks/useStaticViewportHeight';
import { m as motion } from 'framer-motion';
import { css } from '@emotion/react';
const FramedSection = forwardRef(({ backdrop, backdropProps, contentProps, staticViewportHeight = true, children, style, backgroundColor, customHeight, skipTopMargin = false, ...props }, ref) => {
    const height = useStaticViewportHeight(100, { motionValue: true });
    return (React.createElement(Container, { ref: ref, style: {
            ...style,
            height: staticViewportHeight ? height : customHeight ? customHeight : undefined,
        }, ...props },
        React.createElement(BackdropWrapper, { ...backdropProps, skipTopMargin: skipTopMargin },
            React.createElement(Backdrop, { style: { backgroundColor: backgroundColor } }, backdrop)),
        React.createElement(Content, { skipTopMargin: skipTopMargin, ...contentProps }, children)));
});
FramedSection.displayName = 'FramedSection';
export default FramedSection;
const Container = styled(motion.section) `
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;
const BackdropWrapper = styled.div(({ skipTopMargin }) => css(css `
      position: absolute;
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    `, !skipTopMargin &&
    css `
        ${responsiveStyles({
        padding: {
            mobile: Grids.mobile.gutter,
            tablet: Grids.tablet.gutter,
            desktop: Grids.desktop.gutter,
        },
    })}
      `, skipTopMargin &&
    css `
        ${responsiveStyles({
        padding: {
            mobile: `0 ${Grids.mobile.gutter} ${Grids.mobile.gutter} ${Grids.mobile.gutter}`,
            tablet: `0 ${Grids.tablet.gutter} ${Grids.tablet.gutter} ${Grids.tablet.gutter}`,
            desktop: `0 ${Grids.desktop.gutter} ${Grids.desktop.gutter} ${Grids.desktop.gutter}`,
        },
    })}
      `));
const Backdrop = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;

  > * {
    flex: 1;
    object-fit: contain;
  }

  ${responsiveStyles({
    maxWidth: {
        mobile: `100%`,
        desktopWide: `${maximumMediaWidth}px`,
    },
})}
`;
const Content = styled.div(({ skipTopMargin }) => css(css `
      position: relative;
      display: flex;
      flex: 1;
      height: 100%;
      transition: ${createCSSTransition('padding', MotionEasings.easeOut, MotionDurations.fast)};

      ${responsiveStyles({
    maxWidth: {
        mobile: '100%',
        desktopWide: `${maximumWidth}px`,
    },
})}
    `, !skipTopMargin &&
    css `
        ${responsiveStyles({
        padding: {
            mobile: `calc(2 * ${Grids.mobile.gutter}) ${Grids.mobile.gutter}`,
            tablet: `${Spacings.xxLarge} ${Spacings.large}`,
            desktop: `${Spacings.xxxLarge} ${Spacings.xLarge}`,
            desktopWide: `${Spacings.xxxxLarge} ${Spacings.xxxxLarge}`,
        },
        margin: {
            mobile: `${Grids.mobile.gutter}`,
            tablet: `${Grids.tablet.gutter}`,
            desktop: `${Grids.desktop.gutter}`,
        },
    })}
      `, skipTopMargin &&
    css `
        ${responsiveStyles({
        padding: {
            mobile: `${Grids.mobile.gutter} ${Grids.mobile.gutter} calc(2 * ${Grids.mobile.gutter}) ${Grids.mobile.gutter}`,
            tablet: `calc(${Spacings.xxLarge} / 2) ${Spacings.large} ${Spacings.xxLarge} ${Spacings.large}`,
            desktop: `calc(${Spacings.xxxLarge} / 2)${Spacings.xLarge} ${Spacings.xxxLarge} ${Spacings.xLarge}`,
            desktopWide: `calc(${Spacings.xxxxLarge} / 2) ${Spacings.xxxxLarge} ${Spacings.xxxxLarge} ${Spacings.xxxxLarge}`,
        },
        margin: {
            mobile: `${Grids.mobile.gutter}`,
            tablet: `${Grids.tablet.gutter}`,
            desktop: `${Grids.desktop.gutter}`,
        },
    })}
      `));
